/**
 * @fileoverview Zod schemas for the World Building domain.
 *
 * V3 Schemas:
 * - `WorldObjectV3Schema` - World object (artifact/item) with typed fields
 * - `WorldElementV3Schema` - Discriminated union of all V3 element types
 * - `WorldElementTypeV3Enum` - place, set, organization, object, species, system, event
 *
 * All fields use `.describe()` for documentation and AI context.
 */

// =============================================================================
// V3 SCHEMAS - Shared Fields
// =============================================================================

export { SHARED_WORLD_ELEMENT_FIELDS } from './shared-fields';
export { SHARED_WORLD_ELEMENT_FIELD_KEYS } from './shared-fields';

export type { WorldElementFieldDefinition } from './shared-fields';

// =============================================================================
// V3 SCHEMAS - World Object
// =============================================================================

export { WORLD_OBJECT_FIELD_DEFINITIONS } from './world-object';
export { WorldObjectElementEnum } from './world-object';
export { WorldObjectAttributesV3Schema } from './world-object';
export { WorldObjectSnapshotV3Schema } from './world-object';
export { WorldObjectV3Schema } from './world-object';
export { WorldObjectIdeaV3Schema } from './world-object';
export { WorldObjectRefV3Schema } from './world-object';
export { WorldObjectBasicV3Schema } from './world-object';
export { WorldObjectContextV3Schema } from './world-object';

export type { WorldObjectElement } from './world-object';
export type { WorldObjectAttributes } from './world-object';
export type { WorldObjectSnapshotV3 } from './world-object';
export type { WorldObjectV3 } from './world-object';
export type { WorldObjectIdeaV3 } from './world-object';
export type { WorldObjectRefV3 } from './world-object';
export type { WorldObjectBasicV3 } from './world-object';
export type { WorldObjectContextV3 } from './world-object';

// =============================================================================
// V3 SCHEMAS - World Place
// =============================================================================

export { WORLD_PLACE_FIELD_DEFINITIONS } from './world-place';
export { WorldPlaceElementEnum } from './world-place';
export { WorldPlaceAttributesV3Schema } from './world-place';
export { WorldPlaceSnapshotV3Schema } from './world-place';
export { WorldPlaceV3Schema } from './world-place';
export { WorldPlaceIdeaV3Schema } from './world-place';
export { WorldPlaceRefV3Schema } from './world-place';
export { WorldPlaceBasicV3Schema } from './world-place';
export { WorldPlaceContextV3Schema } from './world-place';

export type { WorldPlaceElement } from './world-place';
export type { WorldPlaceAttributes } from './world-place';
export type { WorldPlaceSnapshotV3 } from './world-place';
export type { WorldPlaceV3 } from './world-place';
export type { WorldPlaceIdeaV3 } from './world-place';
export type { WorldPlaceRefV3 } from './world-place';
export type { WorldPlaceBasicV3 } from './world-place';
export type { WorldPlaceContextV3 } from './world-place';

// =============================================================================
// V3 SCHEMAS - World Set
// =============================================================================

export { WORLD_SET_FIELD_DEFINITIONS } from './world-set';
export { WorldSetElementEnum } from './world-set';
export { WorldSetAttributesV3Schema } from './world-set';
export { WorldSetSnapshotV3Schema } from './world-set';
export { WorldSetV3Schema } from './world-set';
export { WorldSetIdeaV3Schema } from './world-set';
export { WorldSetRefV3Schema } from './world-set';
export { WorldSetBasicV3Schema } from './world-set';
export { WorldSetContextV3Schema } from './world-set';

export type { WorldSetElement } from './world-set';
export type { WorldSetAttributes } from './world-set';
export type { WorldSetSnapshotV3 } from './world-set';
export type { WorldSetV3 } from './world-set';
export type { WorldSetIdeaV3 } from './world-set';
export type { WorldSetRefV3 } from './world-set';
export type { WorldSetBasicV3 } from './world-set';
export type { WorldSetContextV3 } from './world-set';

// =============================================================================
// V3 SCHEMAS - World Event
// =============================================================================

export { WORLD_EVENT_FIELD_DEFINITIONS } from './world-event';
export { WorldEventElementEnum } from './world-event';
export { WorldEventAttributesV3Schema } from './world-event';
export { WorldEventSnapshotV3Schema } from './world-event';
export { WorldEventV3Schema } from './world-event';
export { WorldEventIdeaV3Schema } from './world-event';
export { WorldEventRefV3Schema } from './world-event';
export { WorldEventBasicV3Schema } from './world-event';
export { WorldEventContextV3Schema } from './world-event';

export type { WorldEventElement } from './world-event';
export type { WorldEventAttributes } from './world-event';
export type { WorldEventSnapshotV3 } from './world-event';
export type { WorldEventV3 } from './world-event';
export type { WorldEventIdeaV3 } from './world-event';
export type { WorldEventRefV3 } from './world-event';
export type { WorldEventBasicV3 } from './world-event';
export type { WorldEventContextV3 } from './world-event';

// =============================================================================
// V3 SCHEMAS - World Organization
// =============================================================================

export { WORLD_ORGANIZATION_FIELD_DEFINITIONS } from './world-organization';
export { WorldOrganizationElementEnum } from './world-organization';
export { WorldOrganizationAttributesV3Schema } from './world-organization';
export { WorldOrganizationSnapshotV3Schema } from './world-organization';
export { WorldOrganizationV3Schema } from './world-organization';
export { WorldOrganizationIdeaV3Schema } from './world-organization';
export { WorldOrganizationRefV3Schema } from './world-organization';
export { WorldOrganizationBasicV3Schema } from './world-organization';
export { WorldOrganizationContextV3Schema } from './world-organization';

export type { WorldOrganizationElement } from './world-organization';
export type { WorldOrganizationAttributes } from './world-organization';
export type { WorldOrganizationSnapshotV3 } from './world-organization';
export type { WorldOrganizationV3 } from './world-organization';
export type { WorldOrganizationIdeaV3 } from './world-organization';
export type { WorldOrganizationRefV3 } from './world-organization';
export type { WorldOrganizationBasicV3 } from './world-organization';
export type { WorldOrganizationContextV3 } from './world-organization';

// =============================================================================
// V3 SCHEMAS - World Species
// =============================================================================

export { WORLD_SPECIES_FIELD_DEFINITIONS } from './world-species';
export { WorldSpeciesElementEnum } from './world-species';
export { WorldSpeciesAttributesV3Schema } from './world-species';
export { WorldSpeciesSnapshotV3Schema } from './world-species';
export { WorldSpeciesV3Schema } from './world-species';
export { WorldSpeciesIdeaV3Schema } from './world-species';
export { WorldSpeciesRefV3Schema } from './world-species';
export { WorldSpeciesBasicV3Schema } from './world-species';
export { WorldSpeciesContextV3Schema } from './world-species';

export type { WorldSpeciesElement } from './world-species';
export type { WorldSpeciesAttributes } from './world-species';
export type { WorldSpeciesSnapshotV3 } from './world-species';
export type { WorldSpeciesV3 } from './world-species';
export type { WorldSpeciesIdeaV3 } from './world-species';
export type { WorldSpeciesRefV3 } from './world-species';
export type { WorldSpeciesBasicV3 } from './world-species';
export type { WorldSpeciesContextV3 } from './world-species';

// =============================================================================
// V3 SCHEMAS - World System
// =============================================================================

export { WORLD_SYSTEM_FIELD_DEFINITIONS } from './world-system';
export { WorldSystemElementEnum } from './world-system';
export { WorldSystemAttributesV3Schema } from './world-system';
export { WorldSystemSnapshotV3Schema } from './world-system';
export { WorldSystemV3Schema } from './world-system';
export { WorldSystemIdeaV3Schema } from './world-system';
export { WorldSystemRefV3Schema } from './world-system';
export { WorldSystemBasicV3Schema } from './world-system';
export { WorldSystemContextV3Schema } from './world-system';

export type { WorldSystemElement } from './world-system';
export type { WorldSystemAttributes } from './world-system';
export type { WorldSystemSnapshotV3 } from './world-system';
export type { WorldSystemV3 } from './world-system';
export type { WorldSystemIdeaV3 } from './world-system';
export type { WorldSystemRefV3 } from './world-system';
export type { WorldSystemBasicV3 } from './world-system';
export type { WorldSystemContextV3 } from './world-system';

// =============================================================================
// V3 SCHEMAS - Unified Types (Discriminated Union)
// =============================================================================

// =============================================================================
// V3 SCHEMAS - Detection (shared by detector agents)
// =============================================================================

export { ElementClassificationEnum, WorldElementSubtypeEnum } from './world-element-detection';
export { DetectedWorldElementSchema, DetectorOutputSchema } from './world-element-detection';

export type { ElementClassification, WorldElementSubtypeValue } from './world-element-detection';
export type { DetectedWorldElement, DetectorOutput } from './world-element-detection';

// =============================================================================
// V3 SCHEMAS - Unified Types (Discriminated Union)
// =============================================================================

export { WorldElementTypeV3Enum } from './world-element-v3';
export { WORLD_ELEMENT_TYPES_V3 as WORLD_ELEMENT_TYPES_V3_ARRAY } from './world-element-v3';
export { WorldElementV3Schema } from './world-element-v3';
export { WorldElementContextV3Schema } from './world-element-v3';
export { WorldElementSnapshotV3Schema } from './world-element-v3';
export { WorldElementIdeaV3Schema } from './world-element-v3';
export { WorldElementsV3StorageSchema } from './world-element-v3';
export { IdeatedWorldElementsV3StorageSchema } from './world-element-v3';
export { isWorldElementType } from './world-element-v3';

export type { WorldElementTypeV3 } from './world-element-v3';
export type { WorldElementV3 } from './world-element-v3';
export type { WorldElementContextV3 } from './world-element-v3';
export type { WorldElementSnapshotV3 } from './world-element-v3';
export type { WorldElementIdeaV3 } from './world-element-v3';
export type { WorldElementsV3Storage } from './world-element-v3';
export type { IdeatedWorldElementsV3Storage } from './world-element-v3';